import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Field } from "formik";
import FormBody, { InputError, LevelReq } from "../../components/form/FormBody";
import { largeModalShowHide } from "../../features/modalSlice";
import { errorToastify } from "../../utils/toastify.util";
import { createRole, updateRole, getAllMenus } from "../../features/userSlice";
import { roleSchema } from "../../utils/validation.util";

const RoleModal = () => {
  const dispatch = useDispatch();
  const { roleFormData, isBtnLoad, menuList } = useSelector((state) => state.user);

  const [menuSearch, setMenuSearch] = useState("");

  useEffect(() => {
    dispatch(getAllMenus());
  }, [dispatch]);

  const menus = useMemo(() => (Array.isArray(menuList) ? menuList : []), [menuList]);

  const filteredMenus = useMemo(() => {
    const q = (menuSearch || "").trim().toLowerCase();
    if (!q) return menus;
    return menus.filter((m) =>
      String(m?.menu || m?.name || m?.title || "")
        .toLowerCase()
        .includes(q)
    );
  }, [menus, menuSearch]);

  // ✅ initial values mapping (supports your edit response)
  const initialValues = useMemo(() => {
    const base = {
      id: roleFormData?.id || "",
      role: roleFormData?.role || roleFormData?.name || "",
      description: roleFormData?.description || "",
      menuPermissions: [],
    };

    // ✅ Your edit response: menuPermissions has menuid + perms
    if (Array.isArray(roleFormData?.menuPermissions)) {
      base.menuPermissions = roleFormData.menuPermissions.map((p) => ({
        menuid: Number(p?.menuid),
        can_read: Number(p?.can_read) ? 1 : 0,
        can_create: Number(p?.can_create) ? 1 : 0,
        can_delete: Number(p?.can_delete) ? 1 : 0,
      }));
    }

    return base;
  }, [roleFormData]);

  const isEdit = Boolean(initialValues?.id);

  // ✅ IMPORTANT: force Formik reset when switching add/edit
  // edit => "edit-<id>", add => "add"
  const formKey = useMemo(() => {
    return isEdit ? `edit-${initialValues.id}` : "add";
  }, [isEdit, initialValues?.id]);

  // ✅ Reset search box when opening modal for add/edit
  useEffect(() => {
    setMenuSearch("");
  }, [formKey]);

  const onSubmitHandler = (values) => {
    try {
      const role = (values.role || "").trim();
      const description = (values.description || "").trim();

      const mp = Array.isArray(values.menuPermissions) ? values.menuPermissions : [];

      // ✅ Remove items where can_read = 0
      const cleaned = mp
        .map((p) => ({
          menuid: Number(p?.menuid),
          can_read: Number(p?.can_read) ? 1 : 0,
          can_create: Number(p?.can_create) ? 1 : 0,
          can_delete: Number(p?.can_delete) ? 1 : 0,
        }))
        .filter((p) => Number.isFinite(p.menuid) && p.can_read === 1);

      if (!cleaned.length) {
        return errorToastify("Please select at least one menu with Read permission");
      }

      const payload = {
        role,
        description,
        menuPermissions: cleaned,
      };

      if (values.id) {
        dispatch(updateRole({ ...payload, id: values.id }));
      } else {
        dispatch(createRole(payload));
      }

      dispatch(largeModalShowHide());
    } catch {
      errorToastify("Something went wrong");
    }
  };

  return (
    // ✅ widen modal body (optional but recommended)
    <div>
      <FormBody
        key={formKey}              // ✅ THIS FIXES PRESELECT ISSUE
        initialValues={initialValues}
        validationSchema={roleSchema}
        onSubmit={onSubmitHandler}
      >
        {/* Role */}
        <div className="row mb-3">
          <div className="col-md-6">
            <LevelReq htmlFor="role" className="form-label fs-7">
              Role
            </LevelReq>
            <Field type="text" className="form-control" id="role" name="role" />
            <InputError name="role" />
          </div>

          <div className="col-md-6">
            <LevelReq htmlFor="description" className="form-label fs-7">
              Description
            </LevelReq>
            <Field type="text" className="form-control" id="description" name="description" />
            <InputError name="description" />
          </div>
        </div>

        {/* Permissions */}
        <div className="row mb-2">
          <div className="col-md-12">
            <LevelReq className="form-label fs-7">Menu Permissions</LevelReq>

            <input
              type="text"
              className="form-control mb-2"
              placeholder="Search menu..."
              value={menuSearch}
              onChange={(e) => setMenuSearch(e.target.value)}
            />

            <Field name="menuPermissions">
              {({ form }) => {
                const current = Array.isArray(form.values.menuPermissions)
                  ? form.values.menuPermissions
                  : [];

                const getPerm = (menuid) => {
                  const found = current.find((p) => Number(p?.menuid) === Number(menuid));
                  return (
                    found || { menuid: Number(menuid), can_read: 0, can_create: 0, can_delete: 0 }
                  );
                };

                const setPerm = (menuid, patch) => {
                  const id = Number(menuid);
                  const next = [...current];
                  const idx = next.findIndex((p) => Number(p?.menuid) === id);

                  const prev =
                    idx >= 0
                      ? next[idx]
                      : { menuid: id, can_read: 0, can_create: 0, can_delete: 0 };

                  const merged = { ...prev, ...patch, menuid: id };

                  if (idx >= 0) next[idx] = merged;
                  else next.push(merged);

                  form.setFieldValue("menuPermissions", next);
                };

                const toggleRead = (menuid) => {
                  const p = getPerm(menuid);
                  const nextRead = p.can_read ? 0 : 1;

                  setPerm(menuid, {
                    can_read: nextRead,
                    can_create: nextRead ? p.can_create : 0,
                    can_delete: nextRead ? p.can_delete : 0,
                  });
                };

                const toggleCreate = (menuid) => {
                  const p = getPerm(menuid);
                  const nextCreate = p.can_create ? 0 : 1;

                  // ✅ create implies read
                  setPerm(menuid, {
                    can_create: nextCreate,
                    can_read: nextCreate ? 1 : p.can_read,
                  });
                };

                const toggleDelete = (menuid) => {
                  const p = getPerm(menuid);
                  const nextDelete = p.can_delete ? 0 : 1;

                  // ✅ delete implies read
                  setPerm(menuid, {
                    can_delete: nextDelete,
                    can_read: nextDelete ? 1 : p.can_read,
                  });
                };

                return (
                  <div className="border rounded p-2" style={{ maxHeight: 320, overflowY: "auto" }}>
                    <div className="d-flex align-items-center justify-content-between border-bottom pb-2 mb-2" style={{ position: 'sticky', top: '-8px', backgroundColor: '#fff', zIndex: 1 }}>
                      <div className="fw-semibold fs-7">Menu</div>
                      <div className="d-flex gap-4 fw-semibold fs-7">
                        <span style={{ width: 60, textAlign: "center" }}>Read</span>
                        <span style={{ width: 100, textAlign: "center" }}>Create/Edit</span>
                        <span style={{ width: 70, textAlign: "center" }}>Delete</span>
                      </div>
                    </div>

                    {filteredMenus.length ? (
                      filteredMenus.map((m) => {
                        const id = m?.id;
                        const label = m?.menu || m?.name || m?.title || `Menu ${id}`;

                        const p = getPerm(id);
                        const readOn = Number(p.can_read) === 1;

                        return (
                          <div
                            key={`perm-${id}`}
                            className="d-flex align-items-center justify-content-between py-1"
                          >
                            <div className="fs-7" style={{ maxWidth: "55%" }}>
                              {label}
                            </div>

                            <div className="d-flex gap-4">
                              <div style={{ width: 60, textAlign: "center" }}>
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  checked={readOn}
                                  onChange={() => toggleRead(id)}
                                  style={{ width: '18px', height: '18px', border: '2px solid #2b7cff', borderRadius: '4px', appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none', backgroundColor: readOn ? '#2b7cff' : '#fff', boxShadow: readOn ? '0 0 0 2px #2b7cff inset' : '0 0 0 1px #999 inset' }}
                                />
                              </div>

                              <div style={{ width: 100, textAlign: "center" }}>
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  checked={Number(p.can_create) === 1}
                                  disabled={!readOn}
                                  onChange={() => toggleCreate(id)}
                                  style={{ width: '18px', height: '18px', border: '2px solid #2b7cff', borderRadius: '4px', appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none', backgroundColor: Number(p.can_create) === 1 ? '#2b7cff' : '#fff', boxShadow: Number(p.can_create) === 1 ? '0 0 0 2px #2b7cff inset' : '0 0 0 1px #999 inset' }}
                                />
                              </div>

                              <div style={{ width: 70, textAlign: "center" }}>
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  checked={Number(p.can_delete) === 1}
                                  disabled={!readOn}
                                  onChange={() => toggleDelete(id)}
                                  style={{ width: '18px', height: '18px', border: '2px solid #2b7cff', borderRadius: '4px', appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none', backgroundColor: Number(p.can_delete) === 1 ? '#2b7cff' : '#fff', boxShadow: Number(p.can_delete) === 1 ? '0 0 0 2px #2b7cff inset' : '0 0 0 1px #999 inset' }}
                                />
                              </div>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="text-muted fs-7">No menus found</div>
                    )}
                  </div>
                );
              }}
            </Field>
          </div>
        </div>

        <Field type="hidden" id="id" name="id" />

        <div className="col-12 mb-2 mt-3">
          <button
            className="btn btn-primary w-100"
            type={isBtnLoad ? "button" : "submit"}
            disabled={isBtnLoad}
          >
            {isBtnLoad ? (
              <>
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                Loading...
              </>
            ) : (
              <>{isEdit ? "Update Role" : "Create Role"}</>
            )}
          </button>
        </div>
      </FormBody>
    </div>
  );
};

export default RoleModal;
